import { imgixHost } from 'src/config'
import Generic from 'src/models/generic/Generic'
import InventoryRecipe from 'src/models/inventory-recipe/InventoryRecipe'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import SalesChannelRestrictable, { MENU_DEFAULT as SALES_CHANNEL_MENU_DEFAULT } from 'src/models/model/concerns/SalesChannelRestrictable'
import ModifierSet from 'src/models/modifier-set/ModifierSet'

const ACTIONS = {
  DISABLE_SALES: 'disable_sales',
  ENABLE_SALES: 'enable_sales'
}

export default class Modifier extends Model {
  static name = 'Modifier'

  static isConfiguration = true

  static deletable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static duplicatable = true

  static paginate = true

  static hasAuditSummary = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Concession]

  static define () {
    this.concerns = [Archivable, Librariable, SalesChannelRestrictable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      name: {
        type: String,
        required: true,
        translatable: true
      },
      displayOrder: {
        type: Integer,
        default: 100
      },
      image: {
        type: String,
        default: null
      },
      color: {
        type: String,
        default: null
      },
      description: {
        type: String,
        default: null,
        translatable: true
      },
      price: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      cost: {
        type: Number,
        encode: encoders.cents,
        default: null
      },
      disableEditCostCents: {
        type: Boolean,
        output: true,
        excludeFromDefault: true
      },
      salesDisabled: {
        type: Boolean,
        default: false
      },
      modifierSets: {
        type: [ModifierSet],
        reference: true,
        default: () => ([])
      },
      toBeDeleted: {
        type: Boolean,
        output: true,
        default: false
      },
      hasRecipe: {
        type: Boolean,
        output: true,
        default: false
      },
      inventoryRecipes: {
        type: [InventoryRecipe],
        output: true,
        excludeFromDefault: true
      },
      inventoryAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        type: JsonObject,
        output: true,
        excludeFromDefault: true
      },
      locations: {
        type: [Location],
        output: true,
        excludeFromDefault: true
      },
      action: {
        type: String,
        input: true
      }
    }

    this.computed = {
      isActive () {
        return this.isUnarchived && !this.toBeDeleted
      },
      backgroundForImgix () {
        if (this.color) {
          return this.color.slice(1)
        } else {
          return 'FFFFFF'
        }
      },
      imageUrl () {
        if (!this.image) return ''

        return `${imgixHost}/${this.image}?w=100&h100&fit=crop&fill=solid&fill-color=${this.backgroundForImgix}&bg=${this.backgroundForImgix}`
      },
      inventoryStrategy () {
        return this.configuration.currentSite?.activeInventoryStrategy
      },
      nameWithPrice () {
        if (this.price === 0) {
          return this.name
        } else {
          return `${this.name} (${this.i18n.n(this.price, 'currency')})`
        }
      },
      trackStock () {
        return this.hasRecipe || this.action === 'manage_inventory'
      }
    }

    this.queries = {
      modifierAvailabilitiesAndConstraintsByLocationAndRegisterStation: {
        results: {
          value: Generic
        }
      }
    }
  }

  get salesChannelRestrictableChannelsDefault () {
    return SALES_CHANNEL_MENU_DEFAULT
  }

  invalidInventoryRecipeIds (selectedItemVariationId = null) {
    if (!selectedItemVariationId) return []

    /**
     * Recipe triggers that specify an unselected item variation are invalid.
     */
    return _(this.inventoryRecipes.data)
      .map(inventoryRecipe => {
        return _.filter(inventoryRecipe.inventoryRecipeTriggers.data, inventoryRecipeTrigger => {
          return inventoryRecipeTrigger.itemVariationId && inventoryRecipeTrigger.itemVariationId !== selectedItemVariationId
            ? inventoryRecipe.id
            : null
        })
      })
      .flatten()
      .uniq()
      .value()
  }

  async disableSales () {
    this.action = ACTIONS.DISABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async enableSales () {
    this.action = ACTIONS.ENABLE_SALES

    try {
      const { errors } = await this.save()
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      }
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
